import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  Bot,
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  Download,
  FileText,
  Info,
  Loader2,
  Send,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import useAutoResizeTextarea from "../../../shared/hooks/useAutoResizeTextarea";
import MarkdownText from "../../../shared/ui/MarkdownText";
import { extractPmidFromUrl, getPaperStableId } from "../../../shared/utils/paper";
import {
  buildPaperAnalysisPaperRef,
  getPaperAnalysisThread,
  paperAnalysisErrorMessage,
  requestPaperAnalysisTurn,
} from "../services/paperAnalysisService";
import { MAX_PAPER_ANALYSIS_SOURCES } from "../constants/paperAnalysis";
import {
  buildPaperAnalysisExportFilename,
  copyPaperAnalysisMessageToClipboard,
  exportPaperAnalysisMessageToDocx,
} from "../utils/paperAnalysisExport";
import {
  buildPaperAnalysisEngagementEvent,
  enqueueResultEngagementEvent,
} from "../../../services/resultEngagementService";

const toPanelMessages = (messages = []) =>
  messages.map((message) => ({
    id: `history-${message.sequence}`,
    sequence: message.sequence,
    role: message.role,
    content: message.message_content,
    createdAt: message.created_at,
    paperRefs: Array.isArray(message.paper_refs) ? message.paper_refs : [],
  }));

const cleanRefValue = (value, { url = false } = {}) => {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  if (!text) return null;
  return url ? text.replace(/\/+$/, "") : text;
};

const refKeys = (paperRef = {}) => {
  const keys = [];
  const pmid = cleanRefValue(paperRef.pmid || paperRef.PMID);
  const pmcid = cleanRefValue(paperRef.pmcid || paperRef.PMCID);
  const url = cleanRefValue(paperRef.url || paperRef.URL, { url: true });
  if (pmid) keys.push(`pmid:${pmid}`);
  if (pmcid) keys.push(`pmcid:${pmcid.toUpperCase()}`);
  if (url) keys.push(`url:${url}`);
  return keys;
};

const paperRefFromPaper = (paper = {}) => ({
  pmid: paper.pmid || paper.PMID || extractPmidFromUrl(paper.url || paper.URL),
  pmcid: paper.pmcid || paper.PMCID || null,
  url: paper.url || paper.URL || null,
});

const paperDisplayNumber = (paper = {}, index) =>
  paper.rank || paper.Rank || paper.ranking || paper.Ranking || index + 1;

const buildSourceTargetByRefKey = (papers = []) => {
  const targets = new Map();
  papers.forEach((paper, index) => {
    const label = `#${paperDisplayNumber(paper, index)}`;
    const paperId = getPaperStableId(paper, index);
    refKeys(paperRefFromPaper(paper)).forEach((key) => {
      if (!targets.has(key)) {
        targets.set(key, { label, paperId });
      }
    });
  });
  return targets;
};

const messageSourceTargets = (paperRefs = [], sourceTargetByRefKey) => {
  const targets = [];
  const seen = new Set();
  paperRefs.forEach((paperRef) => {
    const target = refKeys(paperRef)
      .map((key) => sourceTargetByRefKey.get(key))
      .find(Boolean);
    if (target && !seen.has(target.label)) {
      seen.add(target.label);
      targets.push(target);
    }
  });
  return targets;
};

const sourceTitle = (paper = {}, fallback) =>
  paper.title || paper.Title || fallback;

const paperAnalysisWelcomeUseCases = (t) => {
  const value = t("paperAnalysis.emptyUseCases", { returnObjects: true });
  if (!Array.isArray(value)) return [];
  return value
    .map((group) => ({
      title: typeof group?.title === "string" ? group.title : "",
      items: Array.isArray(group?.items)
        ? group.items.filter((item) => typeof item === "string" && item.trim())
        : [],
    }))
    .filter((group) => group.title && group.items.length > 0);
};

function PaperAnalysisWelcome({ t, centered = false }) {
  const useCases = paperAnalysisWelcomeUseCases(t);

  return (
    <div
      className={`mx-auto w-full max-w-[700px] text-left ${
        centered ? "flex min-h-full flex-col justify-center py-5" : ""
      }`}
    >
      <div className="space-y-5">
        <div className="flex items-start gap-3">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#eef2fc] text-[#4a6ee0]">
            <FileText className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-bold leading-snug text-[#1f2937] sm:text-lg">
              {t("paperAnalysis.emptyTitle")}
            </h2>
            <p className="mt-1.5 whitespace-pre-line text-sm leading-relaxed text-[#4b5563]">
              {t("paperAnalysis.emptyBody")}
            </p>
            <p className="mt-2 text-[13px] leading-relaxed text-[#6b7280] sm:text-sm">
              {t("paperAnalysis.emptyGuidance")}
            </p>
          </div>
        </div>

        {useCases.length > 0 && (
          <section>
            <h3 className="mb-2.5 text-sm font-semibold text-[#1f2937]">
              {t("paperAnalysis.emptyExamplesTitle")}
            </h3>
            <div className="grid gap-2 md:grid-cols-2">
              {useCases.map((group) => (
                <div
                  key={group.title}
                  className="rounded-lg border border-[#e5e7eb] bg-white px-3.5 py-3"
                >
                  <h4 className="text-sm font-semibold leading-snug text-[#1f2937]">
                    {group.title}
                  </h4>
                  <ul className="mt-2 space-y-1 text-[13px] leading-relaxed text-[#4b5563]">
                    {group.items.map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <span className="mt-[0.45rem] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#c3d2f5]" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

const overviewBlockSnippet = (block = {}) =>
  block.snippet || block.content || block.text || "";

function OverviewLoadingStatus({ label, plain = false }) {
  return (
    <div
      className={`overview-loading-status ${
        plain ? "overview-loading-status--plain" : ""
      } inline-flex items-center rounded-full px-2.5 py-1 text-sm font-medium`}
      role="status"
      aria-live="polite"
    >
      <span className="overview-loading-status__text">{label}</span>
    </div>
  );
}

function PaperAnalysisAssistantLoadingMessage({ label, title }) {
  return (
    <div className="flex flex-col items-start">
      <div
        className="w-fit min-w-[280px] max-w-full rounded-2xl rounded-tl-md border border-slate-200 bg-white px-5 py-4 text-sm leading-6 text-slate-800 shadow-sm sm:min-w-[360px] lg:max-w-4xl"
        role="status"
        aria-live="polite"
      >
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-slate-500">
          <Bot className="h-3.5 w-3.5 text-blue-600" />
          {title}
        </div>
        <div className="text-sm font-medium leading-6 text-blue-500">{label}</div>
      </div>
    </div>
  );
}

function PaperAnalysisGuidance({ guidance, onSelectQuery, t }) {
  const examples = Array.isArray(guidance?.example_queries)
    ? guidance.example_queries.filter((query) => typeof query === "string" && query.trim())
    : [];
  const why = String(guidance?.why_full_text_analysis || "").trim();
  const direction = String(guidance?.analysis_direction || "").trim();

  if (!why || !direction || examples.length !== 4) {
    return null;
  }

  return (
    <section className="rounded-xl border border-blue-100 bg-blue-50/70 px-3.5 py-3 text-sm text-slate-800">
      <div className="flex items-start gap-2.5">
        <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-white text-blue-700 shadow-sm">
          <Bot className="h-3.5 w-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold leading-5 text-slate-950">
            {t("paperAnalysis.guidance.title")}
          </h3>
          <div className="mt-2 space-y-2">
            <div>
              <div className="text-[12px] font-semibold leading-4 text-blue-900">
                {t("paperAnalysis.guidance.whyTitle")}
              </div>
              <p className="mt-0.5 whitespace-pre-wrap text-[13px] leading-5 text-slate-700">
                {why}
              </p>
            </div>
            <div>
              <div className="text-[12px] font-semibold leading-4 text-blue-900">
                {t("paperAnalysis.guidance.directionTitle")}
              </div>
              <p className="mt-0.5 whitespace-pre-wrap text-[13px] leading-5 text-slate-700">
                {direction}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {examples.map((query, index) => (
          <button
            key={`${index}-${query}`}
            type="button"
            onClick={() => onSelectQuery?.(query)}
            className="group flex min-h-[42px] items-center justify-between gap-3 rounded-lg border border-blue-200 bg-white px-3 py-2 text-left text-[13px] font-semibold leading-5 text-slate-800 shadow-sm transition-all hover:-translate-y-0.5 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-900 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
          >
            <span className="min-w-0 flex-1">{query}</span>
            <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600 transition-colors group-hover:bg-blue-600 group-hover:text-white">
              <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}

function OverviewMessageContent({
  overviewData,
  streamContent,
  isGenerating,
  statusLabel,
  availablePapers,
  onSourceLabelClick,
  t,
}) {
  const overviewTextBlocks = Array.isArray(overviewData?.text_blocks)
    ? overviewData.text_blocks
    : [];
  const overviewReferences = Array.isArray(overviewData?.references)
    ? overviewData.references
    : [];
  const overviewText =
    typeof overviewData === "string" ? overviewData.trim() : "";
  const streamText = String(streamContent || "").trim();
  const hasStructuredOverview = overviewTextBlocks.length > 0;
  const hasTextOverview = Boolean(overviewText);
  const hasStream = Boolean(streamText);

  if (hasStream) {
    return (
      <MarkdownText
        content={streamText}
        continuousOrderedLists
      />
    );
  }

  if (hasStructuredOverview) {
    return (
      <div className="space-y-3">
        {overviewTextBlocks.map((block, index) => {
          const snippet = overviewBlockSnippet(block);
          if (!snippet && block.type !== "list") return null;

          if (block.type === "heading") {
            return (
              <h3
                key={`overview-heading-${index}`}
                className="text-sm font-semibold leading-6 text-slate-900"
              >
                <MarkdownText content={snippet} inline />
              </h3>
            );
          }

          if (block.type === "list") {
            const items = Array.isArray(block.list) ? block.list : [];
            return (
              <ul
                key={`overview-list-${index}`}
                className="my-2 list-disc space-y-1 pl-5"
              >
                {items.map((item, itemIndex) => (
                  <li
                    key={`overview-list-${index}-${itemIndex}`}
                    className="whitespace-pre-wrap leading-relaxed"
                  >
                    <MarkdownText
                      content={overviewBlockSnippet(item) || String(item || "")}
                      inline
                    />
                  </li>
                ))}
              </ul>
            );
          }

          return (
            <MarkdownText
              key={`overview-block-${index}`}
              content={snippet}
              continuousOrderedLists
            />
          );
        })}

        {overviewReferences.length > 0 && (
          <div className="mt-4 border-t border-slate-100 pt-3">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              {t("app.aiOverview.sources")}
            </p>
            <div className="grid grid-cols-1 gap-1">
              {overviewReferences.map((ref) => {
                const refIndex = Number(ref.index);
                const isNumericIndex = Number.isFinite(refIndex);
                const displayIndex = isNumericIndex ? refIndex + 1 : ref.index;
                const paper = isNumericIndex ? availablePapers[refIndex] : null;
                const targetPaperId = paper
                  ? getPaperStableId(paper, refIndex)
                  : null;
                return (
                  <button
                    key={`${ref.index}-${ref.title}`}
                    type="button"
                    onClick={() => {
                      if (targetPaperId) {
                        onSourceLabelClick?.(targetPaperId);
                      }
                    }}
                    className="truncate rounded px-2 py-1 text-left text-xs text-slate-500 transition-colors hover:bg-blue-50 hover:text-blue-700"
                    title={ref.title}
                  >
                    [{displayIndex}] {ref.title}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (hasTextOverview) {
    return (
      <MarkdownText
        content={overviewText}
        continuousOrderedLists
      />
    );
  }

  if (isGenerating) {
    return (
      <OverviewLoadingStatus label={statusLabel || t("app.aiOverview.loading")} plain />
    );
  }

  return null;
}

function PaperAnalysisMessage({
  message,
  sourceTargetByRefKey,
  onSourceLabelClick,
  onCopyMessage,
  onExportMessage,
  onFeedbackUp,
  onFeedbackDown,
  isCopied = false,
  feedbackValue = null,
  t,
}) {
  const isUser = message.role === "user";
  const isFeedbackUpSelected = feedbackValue === "up";
  const isFeedbackDownSelected = feedbackValue === "down";
  const canExport =
    !isUser &&
    Boolean(String(message.content || "").trim()) &&
    typeof onCopyMessage === "function" &&
    typeof onExportMessage === "function" &&
    typeof t === "function";
  const sourceTargets =
    isUser && sourceTargetByRefKey
      ? messageSourceTargets(message.paperRefs, sourceTargetByRefKey)
      : [];

  return (
    <div className={`flex flex-col ${isUser ? "items-end" : "items-start"}`}>
      <div
        className={`${
          isUser ? "max-w-[92%]" : "w-fit max-w-full lg:max-w-4xl"
        } rounded-2xl px-3.5 py-2.5 text-sm leading-6 ${
          isUser
            ? "rounded-br-md bg-slate-900 text-white"
            : "rounded-tl-md border border-slate-200 bg-white text-slate-800 shadow-sm"
        }`}
      >
        {isUser ? (
          <div className="whitespace-pre-wrap break-words">{message.content}</div>
        ) : (
          <MarkdownText
            content={message.content}
            className="break-words"
            continuousOrderedLists
            variant="paperAnalysis"
          />
        )}
        {canExport && (
          <div className="mt-3 flex flex-wrap justify-end gap-1.5 border-t border-slate-100 pt-2">
            <button
              type="button"
              onClick={() => onCopyMessage?.(message)}
              className="inline-flex h-7 items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 text-[11px] font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
              title={
                isCopied
                  ? t("paperAnalysis.export.copied")
                  : t("paperAnalysis.export.copy")
              }
              aria-label={
                isCopied
                  ? t("paperAnalysis.export.copied")
                  : t("paperAnalysis.export.copy")
              }
            >
              {isCopied ? (
                <Check className="h-3.5 w-3.5 text-emerald-600" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
              {isCopied
                ? t("paperAnalysis.export.copied")
                : t("paperAnalysis.export.copy")}
            </button>
            <button
              type="button"
              onClick={() => onExportMessage?.(message)}
              className="inline-flex h-7 items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 text-[11px] font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
              title={t("paperAnalysis.export.word")}
              aria-label={t("paperAnalysis.export.word")}
            >
              <Download className="h-3.5 w-3.5" />
              {t("paperAnalysis.export.word")}
            </button>
            <button
              type="button"
              onClick={() => onFeedbackUp?.(message)}
              className={`inline-flex h-7 w-7 items-center justify-center rounded-md border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 ${
                isFeedbackUpSelected
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-slate-200 bg-slate-50 text-slate-600 hover:bg-emerald-50 hover:text-emerald-700"
              }`}
              title={t("paperAnalysis.feedback.up")}
              aria-label={t("paperAnalysis.feedback.up")}
              aria-pressed={isFeedbackUpSelected}
            >
              <ThumbsUp
                className={`h-3.5 w-3.5 ${
                  isFeedbackUpSelected ? "fill-current" : ""
                }`}
              />
            </button>
            <button
              type="button"
              onClick={() => onFeedbackDown?.(message)}
              className={`inline-flex h-7 w-7 items-center justify-center rounded-md border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 ${
                isFeedbackDownSelected
                  ? "border-rose-200 bg-rose-50 text-rose-700"
                  : "border-slate-200 bg-slate-50 text-slate-600 hover:bg-rose-50 hover:text-rose-700"
              }`}
              title={t("paperAnalysis.feedback.down")}
              aria-label={t("paperAnalysis.feedback.down")}
              aria-pressed={isFeedbackDownSelected}
            >
              <ThumbsDown
                className={`h-3.5 w-3.5 ${
                  isFeedbackDownSelected ? "fill-current" : ""
                }`}
              />
            </button>
          </div>
        )}
      </div>
      {sourceTargets.length > 0 && (
        <div className="mt-1 flex max-w-[92%] flex-wrap justify-end gap-1">
          {sourceTargets.map((target) => (
            <button
              key={target.label}
              type="button"
              onClick={() => onSourceLabelClick?.(target.paperId)}
              className="inline-flex min-h-5 items-center rounded-md border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-semibold leading-4 text-blue-700 shadow-sm transition-colors hover:border-blue-300 hover:bg-blue-100 hover:text-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 focus-visible:ring-offset-1"
            >
              {target.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PaperAnalysisOverviewMessage({
  overviewData,
  streamContent,
  isGenerating,
  statusLabel,
  availablePapers,
  onSourceLabelClick,
  t,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasStream = Boolean(String(streamContent || "").trim());
  const shouldExpandDuringGeneration = isGenerating || hasStream;
  const hasOverviewContent =
    hasStream ||
    Boolean(typeof overviewData === "string" && overviewData.trim()) ||
    (Array.isArray(overviewData?.text_blocks) && overviewData.text_blocks.length > 0) ||
    isGenerating;
  const shouldFold = hasOverviewContent && !shouldExpandDuringGeneration && !isExpanded;

  useEffect(() => {
    if (shouldExpandDuringGeneration) {
      setIsExpanded(true);
      return;
    }

    if (hasOverviewContent) {
      setIsExpanded(false);
    }
  }, [hasOverviewContent, shouldExpandDuringGeneration]);

  if (!hasOverviewContent) return null;

  return (
    <div className="flex flex-col items-start">
      <div
        className={`w-fit max-w-full rounded-2xl rounded-tl-md border border-slate-200 bg-white px-5 py-4 text-sm leading-6 text-slate-800 shadow-sm lg:max-w-4xl ${
          shouldExpandDuringGeneration ? "min-w-[280px] sm:min-w-[360px]" : ""
        }`}
      >
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-slate-500">
          <Bot className="h-3.5 w-3.5 text-blue-600" />
          {t("app.aiOverview.title")}
        </div>
        <div className="relative">
          <div className={shouldFold ? "max-h-96 overflow-hidden" : ""}>
            <OverviewMessageContent
              overviewData={overviewData}
              streamContent={streamContent}
              isGenerating={isGenerating}
              statusLabel={statusLabel}
              availablePapers={availablePapers}
              onSourceLabelClick={onSourceLabelClick}
              t={t}
            />
          </div>
          {shouldFold && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-b from-white/0 via-white/90 to-white" />
          )}
        </div>
        {!shouldExpandDuringGeneration && (
          <button
            type="button"
            onClick={() => setIsExpanded((current) => !current)}
            className="group mt-3 flex w-full items-center gap-3 py-1.5 text-xs font-medium text-slate-500 transition-colors hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
            aria-expanded={isExpanded}
          >
            <span className="h-px flex-1 bg-slate-200 transition-colors group-hover:bg-slate-300" />
            <span className="inline-flex items-center gap-1.5">
              {isExpanded ? (
                <ChevronUp className="h-3.5 w-3.5" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5" />
              )}
              {isExpanded
                ? t("app.aiOverview.collapse")
                : t("app.aiOverview.expand")}
            </span>
            <span className="h-px flex-1 bg-slate-200 transition-colors group-hover:bg-slate-300" />
          </button>
        )}
      </div>
    </div>
  );
}

function SourceRow({ paper, index, onRemove, t }) {
  const title = sourceTitle(paper, t("paperAnalysis.untitledPaper"));
  const stableId = getPaperStableId(paper, index);
  const pmid =
    paper.pmid ||
    paper.PMID ||
    extractPmidFromUrl(paper.url || paper.URL) ||
    t("paperAnalysis.unknownPmid");

  return (
    <div
      className="group rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2"
      title={title}
    >
      <div className="flex min-w-0 items-start gap-2">
        <span className="mt-0.5 flex h-5 min-w-5 items-center justify-center rounded-md bg-white text-[11px] font-semibold text-slate-600 ring-1 ring-slate-200">
          {index + 1}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-semibold leading-5 text-slate-800" title={title}>
            {title}
          </div>
          <div className="mt-0.5 break-all text-[11px] leading-4 text-slate-500">
            PMID: {pmid}
          </div>
        </div>
        {onRemove && (
          <button
            type="button"
            onClick={() => onRemove(stableId)}
            className="rounded-md p-1 text-slate-400 opacity-100 transition-colors hover:bg-white hover:text-slate-700 lg:opacity-0 lg:group-hover:opacity-100"
            aria-label={t("paperAnalysis.removeSource")}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function PaperAnalysisPanel({
  papers = [],
  availablePapers = [],
  parentSearchRunId,
  originalQuery,
  searchMode,
  language,
  overviewData = null,
  overviewStreamContent = "",
  isOverviewGenerating = false,
  overviewStatusLabel,
  guidance = null,
  embedded = false,
  onClose,
  onRemoveSource,
  onSourceLabelClick,
  onSourceSelectionRequired,
  showSourceSelectionNudge = false,
  onUsageUpdated,
}) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [streamingContent, setStreamingContent] = useState("");
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState("");
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [feedbackTarget, setFeedbackTarget] = useState(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackByMessageKey, setFeedbackByMessageKey] = useState({});
  const [renderSourceSelectionNudge, setRenderSourceSelectionNudge] = useState(false);
  const [sourceSelectionNudgeExiting, setSourceSelectionNudgeExiting] = useState(false);
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const copyResetTimeoutRef = useRef(null);
  const sourceSelectionNudgeExitTimeoutRef = useRef(null);
  const tRef = useRef(t);

  useAutoResizeTextarea(textareaRef, draft);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  useEffect(
    () => () => {
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current);
      }
      if (sourceSelectionNudgeExitTimeoutRef.current) {
        clearTimeout(sourceSelectionNudgeExitTimeoutRef.current);
      }
    },
    [],
  );

  const sourceCount = papers.length;
  const originalQueryText = String(originalQuery || "").trim();
  const sourceTargetByRefKey = useMemo(
    () =>
      buildSourceTargetByRefKey(
        availablePapers.length > 0 ? availablePapers : papers,
      ),
    [availablePapers, papers],
  );
  const sourceCountLabel = t("paperAnalysis.sourceCount", { count: sourceCount });
  const needsSourceSelection = sourceCount === 0;
  const sourceLimitExceeded = sourceCount > MAX_PAPER_ANALYSIS_SOURCES;
  const sourceSummary = useMemo(() => {
    if (sourceCount === 0) return t("paperAnalysis.sourceRequired");
    return t("paperAnalysis.sourcesSelected", {
      count: sourceCount,
      max: MAX_PAPER_ANALYSIS_SOURCES,
    });
  }, [sourceCount, t]);
  const shouldShowSourceSelectionNudge =
    showSourceSelectionNudge && needsSourceSelection;

  useEffect(() => {
    if (sourceSelectionNudgeExitTimeoutRef.current) {
      clearTimeout(sourceSelectionNudgeExitTimeoutRef.current);
      sourceSelectionNudgeExitTimeoutRef.current = null;
    }

    if (shouldShowSourceSelectionNudge) {
      setRenderSourceSelectionNudge(true);
      setSourceSelectionNudgeExiting(false);
      return undefined;
    }

    setSourceSelectionNudgeExiting(true);
    sourceSelectionNudgeExitTimeoutRef.current = setTimeout(() => {
      setRenderSourceSelectionNudge(false);
      setSourceSelectionNudgeExiting(false);
      sourceSelectionNudgeExitTimeoutRef.current = null;
    }, 320);

    return () => {
      if (sourceSelectionNudgeExitTimeoutRef.current) {
        clearTimeout(sourceSelectionNudgeExitTimeoutRef.current);
        sourceSelectionNudgeExitTimeoutRef.current = null;
      }
    };
  }, [shouldShowSourceSelectionNudge]);

  useEffect(() => {
    let cancelled = false;

    const loadHistory = async () => {
      setMessages([]);
      setStreamingContent("");
      setError("");
      setFeedbackTarget(null);
      setFeedbackText("");
      setFeedbackByMessageKey({});

      if (!parentSearchRunId) {
        return;
      }

      setIsLoadingHistory(true);
      try {
        const thread = await getPaperAnalysisThread({
          searchRunId: parentSearchRunId,
        });
        if (cancelled) return;
        setMessages(toPanelMessages(thread?.messages || []));
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setMessages([]);
          return;
        }
        setError(
          paperAnalysisErrorMessage(
            err,
            tRef.current("paperAnalysis.errors.lookupFailed"),
            tRef.current,
          ),
        );
      } finally {
        if (!cancelled) {
          setIsLoadingHistory(false);
        }
      }
    };

    loadHistory();

    return () => {
      cancelled = true;
    };
  }, [parentSearchRunId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, streamingContent, isSending]);

  const submitTurn = async (event) => {
    event?.preventDefault();
    const nextMessage = draft.trim();
    if (!nextMessage || isSending || isLoadingHistory || isOverviewGenerating) {
      return;
    }
    if (needsSourceSelection) {
      onSourceSelectionRequired?.();
      return;
    }
    if (sourceLimitExceeded) {
      setError(
        t("paperAnalysis.sourceLimitExceeded", {
          count: sourceCount,
          max: MAX_PAPER_ANALYSIS_SOURCES,
        }),
      );
      return;
    }

    const userMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: nextMessage,
      createdAt: new Date().toISOString(),
      paperRefs: papers
        .map((paper) => buildPaperAnalysisPaperRef(paper))
        .filter(
          (paperRef) =>
            paperRef && (paperRef.pmid || paperRef.pmcid || paperRef.url),
        ),
    };
    setDraft("");
    setError("");
    setStreamingContent("");
    setMessages((prev) => [...prev, userMessage]);
    setIsSending(true);

    try {
      const result = await requestPaperAnalysisTurn({
        parentSearchRunId,
        papers,
        message: nextMessage,
        query: originalQuery,
        language,
        searchMode,
        onChunk: (chunk) => {
          setStreamingContent((prev) => `${prev}${chunk}`);
        },
        onUsageUpdated,
      });

      const assistantMessage = {
        id: result?.message?.sequence
          ? `history-${result.message.sequence}`
          : `assistant-${Date.now()}`,
        sequence: result?.message?.sequence,
        role: "assistant",
        content: result?.message?.message_content || "",
        createdAt: result?.message?.created_at || new Date().toISOString(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
      setStreamingContent("");
    } catch (err) {
      setError(paperAnalysisErrorMessage(err, t("paperAnalysis.errors.turnFailed"), t));
    } finally {
      setIsSending(false);
    }
  };

  const canSubmit =
    Boolean(draft.trim()) &&
    !needsSourceSelection &&
    !isLoadingHistory &&
    !isSending &&
    !isOverviewGenerating &&
    !sourceLimitExceeded;
  const canTriggerSourceSelection =
    Boolean(draft.trim()) &&
    needsSourceSelection &&
    !isLoadingHistory &&
    !isSending &&
    !isOverviewGenerating &&
    !sourceLimitExceeded;
  const submitDisabled =
    !Boolean(draft.trim()) ||
    isLoadingHistory ||
    isSending ||
    isOverviewGenerating ||
    sourceLimitExceeded;
  const inputDisabled = isLoadingHistory || isSending || isOverviewGenerating;
  const hasOverviewContent =
    Boolean(String(overviewStreamContent || "").trim()) ||
    Boolean(typeof overviewData === "string" && overviewData.trim()) ||
    (Array.isArray(overviewData?.text_blocks) && overviewData.text_blocks.length > 0) ||
    isOverviewGenerating;
  const hasGuidance =
    Boolean(String(guidance?.why_full_text_analysis || "").trim()) &&
    Boolean(String(guidance?.analysis_direction || "").trim()) &&
    Array.isArray(guidance?.example_queries) &&
    guidance.example_queries.length === 4;
  const hasSyntheticMessages = Boolean(originalQueryText) || hasOverviewContent || hasGuidance;
  const shouldShowWelcome = !error;
  const handleDraftKeyDown = (event) => {
    if (event.key !== "Enter" || !(event.metaKey || event.ctrlKey)) {
      return;
    }
    event.preventDefault();
    submitTurn(event);
  };

  const trackPaperAnalysisEngagement = (eventType, message, extra = {}) => {
    const event = buildPaperAnalysisEngagementEvent({
      eventType,
      searchId: parentSearchRunId,
      query: originalQuery,
      searchMode,
      message,
      ...extra,
    });
    enqueueResultEngagementEvent(event);
  };

  const feedbackKeyForMessage = (message) => {
    const sequence = Number(message?.sequence);
    if (Number.isInteger(sequence) && sequence > 0) {
      return `sequence:${sequence}`;
    }
    return message?.id ? `id:${message.id}` : null;
  };

  const handleCopyMessage = async (message) => {
    try {
      await copyPaperAnalysisMessageToClipboard(message.content);
      trackPaperAnalysisEngagement("paper_analysis_copy", message);
      setCopiedMessageId(message.id);
      setError("");
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current);
      }
      copyResetTimeoutRef.current = setTimeout(() => {
        setCopiedMessageId(null);
      }, 1800);
    } catch (err) {
      console.error("Paper analysis clipboard export failed", err);
      setError(t("paperAnalysis.export.copyFailed"));
    }
  };

  const handleExportMessage = async (message) => {
    try {
      const messageDate = message.createdAt ? new Date(message.createdAt) : new Date();
      const filenameDate = Number.isNaN(messageDate.getTime()) ? new Date() : messageDate;
      await exportPaperAnalysisMessageToDocx({
        content: message.content,
        filename: buildPaperAnalysisExportFilename(filenameDate),
        title: t("paperAnalysis.export.documentTitle"),
      });
      trackPaperAnalysisEngagement("paper_analysis_word_export", message);
      setError("");
    } catch (err) {
      console.error("Paper analysis Word export failed", err);
      setError(t("paperAnalysis.export.wordFailed"));
    }
  };

  const handleFeedbackUp = (message) => {
    const feedbackKey = feedbackKeyForMessage(message);
    if (!feedbackKey || feedbackByMessageKey[feedbackKey] === "up") return;
    trackPaperAnalysisEngagement("paper_analysis_feedback_up", message);
    setFeedbackByMessageKey((prev) => ({
      ...prev,
      [feedbackKey]: "up",
    }));
  };

  const handleFeedbackDown = (message) => {
    const feedbackKey = feedbackKeyForMessage(message);
    if (!feedbackKey || feedbackByMessageKey[feedbackKey] === "down") return;
    setFeedbackTarget(message);
    setFeedbackText("");
  };

  const closeFeedbackDialog = () => {
    setFeedbackTarget(null);
    setFeedbackText("");
  };

  const submitFeedbackDown = () => {
    const trimmedFeedback = feedbackText.trim();
    if (!feedbackTarget || !trimmedFeedback) return;
    const feedbackKey = feedbackKeyForMessage(feedbackTarget);
    if (!feedbackKey) return;
    trackPaperAnalysisEngagement(
      "paper_analysis_feedback_down",
      feedbackTarget,
      { feedbackText: trimmedFeedback },
    );
    setFeedbackByMessageKey((prev) => ({
      ...prev,
      [feedbackKey]: "down",
    }));
    closeFeedbackDialog();
  };

  const handleGuidanceQuerySelect = (nextQuery) => {
    const text = String(nextQuery || "").trim();
    if (!text) return;
    setDraft(text);
    window.requestAnimationFrame(() => {
      textareaRef.current?.focus();
    });
  };

  return (
    <aside
      className={`paper-analysis-panel flex flex-col overflow-hidden ${
        embedded
          ? "h-[calc(100dvh-7rem)] min-h-[calc(100dvh-7rem)] bg-white lg:h-full lg:min-h-0"
          : "h-[min(42rem,calc(100dvh-7rem))] min-h-[24rem] rounded-xl border border-slate-200 bg-slate-50 shadow-sm lg:h-full lg:min-h-0 lg:rounded-none lg:border-0 lg:bg-white lg:shadow-none"
      }`}
    >
      <div className="paper-analysis-panel__header flex-shrink-0 border-b border-slate-200 bg-slate-50 px-4 py-2 sm:py-2.5">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
            <FileText className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h2 className="truncate text-sm font-semibold text-slate-950">
                {t("paperAnalysis.title")}
              </h2>
              <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                {sourceCountLabel}
              </span>
            </div>
            <p className="mt-0.5 text-xs leading-5 text-slate-500">{sourceSummary}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
            aria-label={t("paperAnalysis.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {sourceLimitExceeded && (
          <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
            {t("paperAnalysis.sourceLimitExceeded", {
              count: sourceCount,
              max: MAX_PAPER_ANALYSIS_SOURCES,
            })}
          </div>
        )}
      </div>

      <div className="paper-analysis-panel__messages min-h-0 flex-1 overflow-y-auto px-3 py-3 sm:px-4 sm:py-4">
        {isLoadingHistory ? (
          <div className="flex h-full items-center justify-center text-sm text-slate-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t("paperAnalysis.loadingHistory")}
          </div>
        ) : shouldShowWelcome && !hasSyntheticMessages && messages.length === 0 ? (
          <PaperAnalysisWelcome t={t} centered />
        ) : (
          <div className="space-y-3">
            {shouldShowWelcome && (
              <PaperAnalysisWelcome t={t} />
            )}
            {originalQueryText && (
              <PaperAnalysisMessage
                message={{
                  id: "original-query",
                  role: "user",
                  content: originalQueryText,
                  paperRefs: [],
                }}
                sourceTargetByRefKey={sourceTargetByRefKey}
                onSourceLabelClick={onSourceLabelClick}
                t={t}
              />
            )}
            <PaperAnalysisOverviewMessage
              overviewData={overviewData}
              streamContent={overviewStreamContent}
              isGenerating={isOverviewGenerating}
              statusLabel={overviewStatusLabel}
              availablePapers={availablePapers}
              onSourceLabelClick={onSourceLabelClick}
              t={t}
            />
            <PaperAnalysisGuidance
              guidance={guidance}
              onSelectQuery={handleGuidanceQuerySelect}
              t={t}
            />
            {messages.map((message) => (
              <PaperAnalysisMessage
                key={message.id}
                message={message}
                sourceTargetByRefKey={sourceTargetByRefKey}
                onSourceLabelClick={onSourceLabelClick}
                onCopyMessage={handleCopyMessage}
                onExportMessage={handleExportMessage}
                onFeedbackUp={handleFeedbackUp}
                onFeedbackDown={handleFeedbackDown}
                isCopied={copiedMessageId === message.id}
                feedbackValue={
                  feedbackByMessageKey[feedbackKeyForMessage(message)] || null
                }
                t={t}
              />
            ))}
            {streamingContent && (
              <PaperAnalysisMessage
                message={{ id: "streaming", role: "assistant", content: streamingContent }}
                sourceTargetByRefKey={sourceTargetByRefKey}
                onSourceLabelClick={onSourceLabelClick}
                t={t}
              />
            )}
            {isSending && !streamingContent && (
              <PaperAnalysisAssistantLoadingMessage
                title={t("paperAnalysis.title")}
                label={t("paperAnalysis.thinking")}
              />
            )}
            <div ref={messagesEndRef} />
          </div>
        )}

        {error && (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>

      <form
        onSubmit={submitTurn}
        className="paper-analysis-panel__input-form flex-shrink-0 border-t border-slate-200 bg-white p-2.5 sm:p-3"
      >
        {renderSourceSelectionNudge && (
          <div
            className={`paper-analysis-input-nudge mb-2 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs font-semibold leading-5 text-amber-900 shadow-sm ${
              sourceSelectionNudgeExiting ? "paper-analysis-input-nudge--exit" : ""
            }`}
            role="status"
            aria-live="polite"
          >
            <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
            <span>{t("paperAnalysis.sourceSelectionNudgeMessage")}</span>
          </div>
        )}
        <div className="flex items-end gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 focus-within:border-blue-300 focus-within:bg-white">
          <textarea
            ref={textareaRef}
            rows={1}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={handleDraftKeyDown}
            placeholder={
              isOverviewGenerating
                ? t("paperAnalysis.overviewGeneratingPlaceholder")
                : t("paperAnalysis.placeholder")
            }
            disabled={inputDisabled}
            className="max-h-32 min-h-[36px] flex-1 resize-none bg-transparent py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 disabled:cursor-not-allowed"
          />
          <button
            type="submit"
            disabled={submitDisabled}
            aria-disabled={!canSubmit}
            className={`mb-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg transition-colors ${
              canSubmit
                ? "bg-slate-900 text-white hover:bg-slate-800"
                : canTriggerSourceSelection
                  ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                : "cursor-not-allowed bg-slate-200 text-slate-400"
            }`}
            aria-label={t("paperAnalysis.send")}
          >
            {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
        {sourceCount > 0 && (
          <div className="paper-analysis-panel__source-tray mt-2 overflow-y-auto pr-1">
            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 xl:grid-cols-4">
              {papers.map((paper, index) => (
                <SourceRow
                  key={getPaperStableId(paper, index) || index}
                  paper={paper}
                  index={index}
                  onRemove={onRemoveSource}
                  t={t}
                />
              ))}
            </div>
          </div>
        )}
        <div className="paper-analysis-panel__input-help mt-1 px-1 text-[11px] leading-4 text-slate-400">
          {t("paperAnalysis.sendShortcut")}
        </div>
        <div
          className={`paper-analysis-panel__input-notice mt-2 flex items-start gap-1.5 rounded-lg border px-2.5 py-2 text-[11px] leading-4 ${
            needsSourceSelection
              ? "border-amber-200 bg-amber-50 text-amber-900"
              : "border-blue-100 bg-blue-50 text-blue-800"
          }`}
        >
          <Info
            className={`mt-0.5 h-3.5 w-3.5 flex-shrink-0 ${
              needsSourceSelection ? "text-amber-600" : "text-blue-600"
            }`}
          />
          <span>
            {needsSourceSelection
              ? t("paperAnalysis.sourceRequired")
              : t("paperAnalysis.inputNotice")}
          </span>
        </div>
      </form>
      {feedbackTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
          <div
            className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-4 shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="paper-analysis-feedback-title"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3
                  id="paper-analysis-feedback-title"
                  className="text-sm font-semibold text-slate-900"
                >
                  {t("paperAnalysis.feedback.title")}
                </h3>
                <p className="mt-1 text-xs leading-5 text-slate-500">
                  {t("paperAnalysis.feedback.description")}
                </p>
              </div>
              <button
                type="button"
                onClick={closeFeedbackDialog}
                className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
                aria-label={t("paperAnalysis.feedback.cancel")}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <textarea
              value={feedbackText}
              onChange={(event) => setFeedbackText(event.target.value)}
              rows={4}
              className="mt-3 w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
              placeholder={t("paperAnalysis.feedback.placeholder")}
            />
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeFeedbackDialog}
                className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900"
              >
                {t("paperAnalysis.feedback.cancel")}
              </button>
              <button
                type="button"
                onClick={submitFeedbackDown}
                disabled={!feedbackText.trim()}
                className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {t("paperAnalysis.feedback.submit")}
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
